/**
 * useNetworkStatus Hook
 *
 * Monitors network connectivity and provides online/offline status.
 * Listens to browser online/offline events and updates state accordingly.
 */

import { useState, useEffect } from 'react';
import { logger } from '../utils/logger';

export interface NetworkStatus {
  /** Current online status */
  isOnline: boolean;
  /** Whether the connection was previously offline (useful for showing "back online" messages) */
  wasOffline: boolean;
}

/**
 * Custom hook to monitor network status
 */
export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    // Initialize with current navigator status
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  });

  const [wasOffline, setWasOffline] = useState<boolean>(false);

  useEffect(() => {
    // Handler for online event
    const handleOnline = () => {
      logger.info('Network connection restored');
      setIsOnline(true);
      setWasOffline(true);

      // Reset wasOffline after showing the message
      setTimeout(() => {
        setWasOffline(false);
      }, 5000);
    };

    // Handler for offline event
    const handleOffline = () => {
      logger.warn('Network connection lost');
      setIsOnline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline, wasOffline };
}

export default useNetworkStatus;
